import { createRetryFunction } from "../helper/Extensions";
import { formatBigWithLocale } from "../helper/Parser";
import { addNotification } from "../notification/Notificationstore";
import { addQueueTask } from "./QueueTaskStore.svelte";
import { systemInformationStore } from "./SystemInformationStore.svelte";
import { _ } from "svelte-i18n";
import { get, writable } from "svelte/store";
import { getInitialData, selectedClientStore } from "../api/EnzianDataApi";
import { createClient, deleteClient, getClients, patchClient, patchSettingDocumentSetting } from "../api/EnzianSettingsApi";
import LoadingStore from "../basic/loading/LoadingStore";
import { handleStoreError } from "../stores/GeneralStore";
export let clientStore = writable([]);
export let showClientSwitcher = writable(false);
/**
 * GENERAL DATA CONTAINERS
 */
export const systemPositionTypeStore = writable([]);
export const settingDocumentSettingStore = writable(null);
export const factorOptions = writable([]);
/**
 * VIEW STATE STORES
 */
export const showCreateClientModal = writable(false);
export const LOCAL_STORAGE_CLIENT_ID = "selectedClientId";
let clientLoadingPromise = null;
//We need to load the available clients.
export async function loadCleintsStore() {
    //We are already loading the client
    if (clientLoadingPromise) {
        return clientLoadingPromise;
    }
    clientLoadingPromise = getClients();
    const data = await clientLoadingPromise;
    clientStore.set(data);
    //Get the last selected id from the cookies or the first client.
    let lastSelectedClientId = data[0].id;
    try {
        //Get the client id from the local storage.
        const remeberedClientId = localStorage.getItem(LOCAL_STORAGE_CLIENT_ID);
        if (remeberedClientId) {
            lastSelectedClientId = parseInt(remeberedClientId);
        }
    }
    catch {
        //In case of error set the default one.
        lastSelectedClientId = data[0].id;
    }
    const selectedClient = data.find((client) => client.id == lastSelectedClientId) || data[0];
    //Set the client.
    selectedClientStore.set(selectedClient);
    //Load data necessary for the document.
    await loadSetupData();
    //Clear the promise again
    clientLoadingPromise = null;
}
/**
 * Load data necessary for the documents and settings.
 */
export async function loadSetupData() {
    const initalData = await createRetryFunction(getInitialData)();
    //Set the new value to the store.
    systemPositionTypeStore.set(initalData.systemPositionTypes);
    settingDocumentSettingStore.set(initalData.settingDocumentSetting);
    systemInformationStore.set(initalData.systemInformation);
    //Make sure we have the data ready for the dropdown
    prepareFactorOptions(initalData.settingDocumentSetting);
}
function prepareFactorOptions(settingDocumentSetting) {
    //transform the settingCalculationFactorGroup to a usable form
    let factorOptionsLocal = [];
    for (let i = 0; i < 10; i++) {
        const factor = settingDocumentSetting["calculationFactor" + i];
        factorOptionsLocal.push({
            index: i,
            defaultName: "KF" +
                (i + 1) +
                " " +
                formatBigWithLocale(factor),
        });
    }
    //Set the transformed valued
    factorOptions.set(factorOptionsLocal);
}
/**
 * Create a new client and set it as active.
 */
export async function createClientStore(request) {
    let errorFound = false;
    if (request.name.length <= 2) {
        //Show a info to the user
        addNotification({
            type: "info",
            message: get(_)("client.create.err_name_to_short")
        });
        errorFound = true;
    }
    if (request.shortName?.trim().length !== 2) {
        //Show a info to the user
        addNotification({
            type: "info",
            message: get(_)("client.create.err_short_name_not_set")
        });
        errorFound = true;
    }
    if (errorFound)
        return false;
    const newClient = await createClient(request);
    //Insert the new client to the list and sort it with the given short name
    clientStore.update(list => {
        return [...list, newClient].sort((a, b) => b.shortName.localeCompare(a.shortName));
    });
    //Set the new client as active
    await switchClientStore(newClient);
    return true;
}
/**
 * Update the values on the client.
 * @param request
 */
export async function patchSelectedClientStore(request) {
    const client = get(selectedClientStore);
    const updatedClient = await patchClient(request);
    if (client == null)
        return;
    clientStore.update(oldList => {
        const index = oldList.findIndex(item => item.id == client.id);
        if (index < 0)
            return oldList;
        oldList[index] = updatedClient;
        return oldList;
    });
    selectedClientStore.set(updatedClient);
}
/**
 * Delte a client from the backend.
 * This will delte all the documents and everything connected to this client.
 */
export async function deleteClientStore() {
    const targetClient = get(selectedClientStore);
    var clientList = get(clientStore);
    if (targetClient == null)
        return;
    try {
        LoadingStore.set(true);
        if (clientList.length <= 1) {
            addNotification({
                type: "info",
                message: get(_)("settings.basic.client.err_delete_last_cleint")
            });
            return;
        }
        //Start the delete of the client
        const queueTask = await deleteClient();
        //Add the task to the stack
        addQueueTask(queueTask);
        //Remove the client from the list
        clientStore.update(oldList => {
            return oldList.filter(client => client.id != targetClient.id);
        });
        //Filter the client from the selection list.
        clientList = clientList.filter(client => client.id != targetClient.id);
        //Select a new client
        selectedClientStore.set(clientList[0]);
    }
    catch (ex) {
        //Inform the user about the error
        handleStoreError("settings.basic.client.err_delete_client", ex);
    }
    finally {
        //Hide the loading screen again.
        LoadingStore.set(false);
    }
}
export async function switchClientStore(client) {
    //Hide the popup
    showClientSwitcher.set(false);
    //Save the selected client to the cookies.
    localStorage.setItem(LOCAL_STORAGE_CLIENT_ID, client.id.toString());
    selectedClientStore.set(client);
    await loadSetupData();
}
/**
 * Update existing default document settings.
 */
export async function patchSettingDocumentSettingStore(request) {
    const updatedValue = await patchSettingDocumentSetting(request);
    //Set the new values
    settingDocumentSettingStore.set(updatedValue);
    //Make sure we have the data ready for the dropdown
    prepareFactorOptions(updatedValue);
}
